import { Eye, EyeOff, MoreHorizontal, Plus, Copy, Trash2, Code2, Sparkles, PanelTop, PanelBottom } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import SortableList from "./SortableList";
import { PanelHeader } from "./PagesPanel";
import { useEditor } from "./useEditorState";
import { ORIGINS } from "@/lib/constants";

export function OriginBadge({ origin, type }) {
  const o = ORIGINS[origin] || ORIGINS.manual;
  return <span className={`text-[9.5px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wide ${type === "html" ? "bg-amber-50 text-amber-700" : o.cls}`}>{type === "html" ? "HTML" : o.label}</span>;
}

function GlobalRow({ comp, icon: Icon }) {
  const { selection, setSelection } = useEditor();
  return (
    <button onClick={() => setSelection({ kind: "component", id: comp.id })} data-testid={`global-component-${comp.name}`}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-xs text-slate-600 border border-dashed ${selection?.id === comp.id ? "border-sky-400 bg-sky-50" : "border-slate-200 hover:bg-slate-50"}`}>
      <Icon size={14} className="text-slate-400" /><span className="truncate">{comp.name}</span><span className="ml-auto text-[10px] text-slate-400">globale</span>
    </button>
  );
}

export default function SectionsPanel() {
  const ed = useEditor();
  const { content, pageId, selection, setSelection, diagnostics, setUi, isDesktop } = ed;
  const page = content.pages.find((p) => p.id === pageId);
  if (!page) return null;
  const errIds = new Set(diagnostics.map((d) => d.id));
  const selIndex = page.sections.findIndex((s) => s.id === selection?.id);
  const openAdd = () => setUi({ addSection: { pageId: page.id, index: selIndex >= 0 ? selIndex + 1 : page.sections.length } });
  const select = (s, tab) => { setSelection({ kind: "section", id: s.id }); setUi(isDesktop ? { rightTab: tab || "props", rightOpen: !!tab || undefined } : { rightTab: tab || "props", leftOpen: false, rightOpen: true }); };
  const hiddenHere = page.hide_components || [];
  const header = content.components.filter((c) => c.placement === "header" && !hiddenHere.includes(c.id));
  const footer = content.components.filter((c) => c.placement === "footer" && !hiddenHere.includes(c.id));
  return (
    <div data-testid="sections-panel">
      <PanelHeader title="Sezioni" subtitle={page.name} action={<Button size="sm" className="h-8 bg-sky-600 hover:bg-sky-700" onClick={openAdd} data-testid="add-section-btn"><Plus size={14} className="mr-1" />Aggiungi</Button>} />
      <div className="px-2 space-y-1.5">
        {header.map((c) => <GlobalRow key={c.id} comp={c} icon={PanelTop} />)}
        {page.sections.length === 0 && <p className="px-3 py-6 text-xs text-slate-500 text-center border border-dashed rounded-md">Nessuna sezione. Aggiungine una dalla libreria, incolla codice o chiedi all'AI.</p>}
        <SortableList items={page.sections} onMove={(a, b) => ed.moveSection(page.id, a, b)} testIdPrefix="section-row" render={(s) => (
          <div className={`group flex items-center gap-1 rounded-md pr-1 ${selection?.id === s.id ? "bg-sky-50 ring-1 ring-sky-400" : "hover:bg-slate-100"}`}>
            <button className="flex-1 min-w-0 text-left px-2 py-2" onClick={() => select(s)} data-testid={`section-item-${s.id}`}>
              <p className={`text-sm truncate ${s.hidden ? "text-slate-400 line-through" : "text-slate-800"}`} title={s.name}>{s.name}</p>
              <div className="mt-1 flex items-center gap-1.5"><OriginBadge origin={s.origin} type={s.type} />{errIds.has(s.id) && <span className="text-[10px] text-red-600 font-medium">errore</span>}</div>
            </button>
            <button className="p-1.5 text-slate-400 hover:text-slate-800" onClick={() => ed.updateSection(s.id, { hidden: !s.hidden })} aria-label="Visibilità" data-testid={`section-visibility-${s.id}`}>
              {s.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger className="p-1.5 rounded text-slate-400 hover:text-slate-800" data-testid={`section-menu-${s.id}`}><MoreHorizontal size={15} /></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => select(s, "code")}><Code2 size={14} className="mr-2" />Modifica codice</DropdownMenuItem>
                <DropdownMenuItem onClick={() => select(s, "ai")}><Sparkles size={14} className="mr-2" />Modifica con AI</DropdownMenuItem>
                <DropdownMenuItem onClick={() => { const id = ed.duplicateSection(s.id); setSelection({ kind: "section", id }); }} data-testid={`section-duplicate-${s.id}`}><Copy size={14} className="mr-2" />Duplica</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-red-600" onClick={() => setUi({ confirm: { title: `Eliminare la sezione "${s.name}"?`, onConfirm: () => { ed.deleteSection(s.id); setSelection(null); } } })} data-testid={`section-delete-${s.id}`}><Trash2 size={14} className="mr-2" />Elimina</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )} />
        {footer.map((c) => <GlobalRow key={c.id} comp={c} icon={PanelBottom} />)}
      </div>
    </div>
  );
}
